import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Button, Table, Card, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { faFlask } from '@fortawesome/free-solid-svg-icons';
import { faStop } from '@fortawesome/free-solid-svg-icons';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { apiGet, apiPost } from '../../services/AuthService.js';
import { COLORS, btnGold, statusLabel, adminStickyThStyle } from '../../styles/goldTheme.js';
import DiffLogViewer from './DiffLogViewer.js';

// ============================================================================
// Regression Tests — Admin Dashboard tab
// ============================================================================
// Replaces the manual SQL workflow in
//   Database_Scripts/Ad Hoc Queries/Regression Testing/RUN_RegressionTests.sql
// which required flipping TestDefinition.IsActive to choose tests, hand-inserting a Process 14 row,
// dropping a "refresh" message on the import queue in Storage Explorer, then polling TestRun /
// TestItem with more SQL.
//
// Selection is by explicit TestDefinitionId, so nothing has to be mutated to run a single test.
// "Bulk" vs "Single" is parsed server-side off the "[Bulk Test] " / "[Single Test] " prefix each
// TestDescription carries — that prefix IS the classification; there is no column for it.
// ============================================================================

// TestStatus table ids. Runs and items share this vocabulary.
//   1 Processing | 2 Process Fail | 3 Verifying | 4 Verify Fail | 5 Success | 6 Fail
const TEST_STATUS = {
    PROCESSING: 1,
    PROCESS_FAIL: 2,
    VERIFYING: 3,
    VERIFY_FAIL: 4,
    SUCCESS: 5,
    FAIL: 6
};

const STATUS_STYLES = {
    [TEST_STATUS.PROCESSING]: { bg: '#66B2FF', fg: '#003C9D', label: 'Processing' },
    [TEST_STATUS.PROCESS_FAIL]: { bg: '#E87979', fg: '#921E1E', label: 'Process Fail' },
    [TEST_STATUS.VERIFYING]: { bg: '#F2B441', fg: '#7A5A12', label: 'Verifying' },
    [TEST_STATUS.VERIFY_FAIL]: { bg: '#E87979', fg: '#921E1E', label: 'Verify Fail' },
    [TEST_STATUS.SUCCESS]: { bg: '#73D68E', fg: '#1B602D', label: 'Success' },
    [TEST_STATUS.FAIL]: { bg: '#E87979', fg: '#921E1E', label: 'Fail' }
};

// Not a TestStatus row. The service flips a TestRun to Fail (6) the moment ANY item fails, while the
// rest are still converting, so a run's own statusId reads 'Fail' mid-flight. A definition that is
// in InFlightRuns is rendered with this instead; the real Fail/Success pill takes over once the run
// reaches a terminal status.
const RUNNING_STYLE = { bg: '#66B2FF', fg: '#003C9D', label: 'Running' };

// Poll fast enough to feel live, slow enough not to hammer the API across a run that can take
// many minutes. Only runs while something is actually in flight.
const POLL_INTERVAL_MS = 4000;

// How long a transient action banner stays up before clearing itself.
const BANNER_TIMEOUT_MS = 10000;

// The "run in progress" notice is only a heads-up when a run starts; the Running now table carries the
// detail after that, so the notice hides itself after a few seconds.
const RUN_NOTICE_TIMEOUT_MS = 7000;

function StatusPill({ statusId, statusName }) {
    const style = STATUS_STYLES[statusId];
    if (!style) {
        return <span style={{ ...statusLabel, backgroundColor: '#e5e7eb', color: '#4b5563' }}>{statusName || '—'}</span>;
    }

    return (
        <span style={{ ...statusLabel, backgroundColor: style.bg, color: style.fg, fontWeight: 600, fontSize: '0.78rem' }}>
            {statusName || style.label}
        </span>
    );
}

// Relative day label + wall-clock time — "Today 10:22:39 PM", "Yesterday 9:14:02 AM",
// "Last Monday 4:31:07 PM" — falling back to an absolute date once a run is more than a week old,
// where "Last Tuesday" stops being a useful way to locate something.
//
// Day bucketing is done on calendar-day boundaries, not on elapsed hours: 12:05am is "Today" and
// 11:55pm the night before is "Yesterday", which is how someone reading the column thinks about it.
// Run and item times are stored in UTC. The API marks them with "Z"; a zone-less ISO string from an
// older API build is UTC too, so read it that way and let the browser show the viewer's local time.
// Returns null for a missing or unreadable value.
function parseApiDate(value) {
    if (!value) return null;
    const isZoneless = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value);
    const parsed = new Date(isZoneless ? `${value}Z` : value);
    return isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
    const parsed = parseApiDate(value);
    if (!parsed) return '—';

    const time = parsed.toLocaleTimeString(undefined, {
        hour: 'numeric', minute: '2-digit', second: '2-digit'
    });

    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const daysAgo = Math.round((startOfDay(new Date()) - startOfDay(parsed)) / 86400000);

    if (daysAgo === 0) return `Today ${time}`;
    if (daysAgo === 1) return `Yesterday ${time}`;

    // 2-6 days back is still within "last <weekday>" reach; 7+ would be ambiguous with this week.
    if (daysAgo > 1 && daysAgo < 7) {
        return `Last ${parsed.toLocaleDateString(undefined, { weekday: 'long' })} ${time}`;
    }

    // Future timestamps (clock skew between the API and the browser) shouldn't read as "Last Friday".
    if (daysAgo < 0) return `${parsed.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: '2-digit' })} ${time}`;

    return `${parsed.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: '2-digit' })} ${time}`;
}

// How long a finished test stays in the "Recently completed" card before dropping back into the Test List.
const RECENTLY_COMPLETED_MINUTES = 30;

// Compact "10/15 2:04 PM" for the run history chips (the full date is in the chip's tooltip).
function formatShortDateTime(value) {
    const parsed = parseApiDate(value);
    if (!parsed) return '—';
    return `${parsed.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })} ${parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

// How many recent runs of each test the history strip shows (and the time-left estimate draws on).
const RUN_HISTORY_TAKE = 10;

// A run's length: first file admitted to last file finished. Measured from the first file, not from when the run
// was created, because a run can wait behind older runs for a file slot first. LastActivity is the newest file's
// status date; a run with no files yet falls back to its own dates. null when either end is unknown.
function runDurationMs(run) {
    const start = parseApiDate(run?.firstItemStartDate) || parseApiDate(run?.startDate);
    const end = parseApiDate(run?.lastActivity) || parseApiDate(run?.statusDate);
    if (!start || !end || end < start) return null;
    return end.getTime() - start.getTime();
}

// "48s", "12m", "1h 05m".
function formatDuration(ms) {
    if (ms == null || ms < 0) return '—';
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const totalMinutes = Math.round(totalSeconds / 60);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    return `${hours}h ${String(totalMinutes % 60).padStart(2, '0')}m`;
}

// What a full run of this test usually takes: the median of its last five runs that finished on their own
// (not cancelled or stalled) and covered every file. null when there is no such run to go on.
function typicalDurationMs(runs, fileCount) {
    const durations = (runs || [])
        .filter(run => run.isComplete && !run.wasCancelled && !run.isStalled && (!fileCount || run.itemCount >= fileCount))
        .slice(0, 5)
        .map(runDurationMs)
        .filter(ms => ms != null)
        .sort((a, b) => a - b);
    if (durations.length === 0) return null;
    const middle = Math.floor(durations.length / 2);
    return durations.length % 2 ? durations[middle] : (durations[middle - 1] + durations[middle]) / 2;
}

// Remaining cooldown as something readable on a button: seconds while that is meaningful, then
// whole minutes. Rounds UP so the label never says "0m" while the button is still disabled.
function describeCooldown(seconds) {
    if (seconds <= 0) return '';
    if (seconds < 60) return `${seconds}s`;
    return `${Math.ceil(seconds / 60)}m`;
}

// The harness writes progress into StatusNotes and errors into ErrorMessage (UpdateTestItemAsync
// swaps which column it touches based on whether the status is an error), so the message worth
// showing is whichever one matches the current status.
function getItemMessage(item) {
    const isError = item.statusId === TEST_STATUS.PROCESS_FAIL
        || item.statusId === TEST_STATUS.VERIFY_FAIL
        || item.statusId === TEST_STATUS.FAIL;

    return (isError ? item.errorMessage : item.statusNotes) || item.statusNotes || '';
}

export default function RegressionTests() {
    const { getAccessTokenSilently } = useAuth0();

    const [definitions, setDefinitions] = useState([]);
    const [inFlightRuns, setInFlightRuns] = useState([]);
    const [runStatus, setRunStatus] = useState({ isRunning: false });

    const [loading, setLoading] = useState(true);
    const [launching, setLaunching] = useState(false);
    const [downloadingDiffLogs, setDownloadingDiffLogs] = useState(false);
    const [runNoticeVisible, setRunNoticeVisible] = useState(false);
    // Tests just launched from this tab, keyed by id -> time clicked. They count as running at once, so the
    // row moves to Running now and its play button disappears before the server has even answered.
    const [pendingLaunches, setPendingLaunches] = useState({});
    // Synchronous guard: React state updates are not applied until the next render, so a fast double
    // click could otherwise send two launch requests before the buttons disable.
    const launchInFlightRef = useRef(false);
    const lastNoticedProcessId = useRef(null);
    const [cancelling, setCancelling] = useState(false);
    const [updatingDefinitions, setUpdatingDefinitions] = useState(false);
    const [banner, setBanner] = useState(null); // { variant, message }
    const [loadError, setLoadError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    // Whether this environment's test definitions already match UPDATE_TestDefinitions.sql, from a dry run
    // on the API (the script is run in a transaction that is always rolled back). null = not known yet, or
    // the check failed - the Update button is then left enabled rather than wrongly blocked.
    const [definitionsStatus, setDefinitionsStatus] = useState(null);
    // Ticks every 30 s so a "Recently completed" test drops back into the Test List once its time is up,
    // even when nothing is being polled.
    const [nowTick, setNowTick] = useState(() => Date.now());
    // Per-section column sort: { column, direction } or null for the default (most recently updated first).
    const [sortBy, setSortBy] = useState({ running: null, recent: null, list: null, legacy: null });
    const [activeFilters, setActiveFilters] = useState([]);
    // This environment's ZZTEST accounts checked against the baseline the controls were made with (GET ConfigCheck):
    // { environment, ok, failures, checks: [{ area, item, expected, actual, ok }] }, or null when not known.
    const [configCheck, setConfigCheck] = useState(null);
    const [showConfigDetails, setShowConfigDetails] = useState(false);
    // Recent runs of every test, newest first, keyed by test definition id (GET RunHistory, no file rows).
    const [runHistory, setRunHistory] = useState({});
    // The file whose diff log is open in the viewer, or null.
    const [diffViewerItem, setDiffViewerItem] = useState(null);

    // Kept in a ref so the polling effect can read the latest value without re-subscribing
    // (and restarting the timer) on every tick.
    const isPollingActiveRef = useRef(false);
    // Numbers each loadAll so a slow response that lands after a newer one is thrown away instead of
    // briefly putting back a pre-launch view (which re-enabled a just-launched test's play button).
    const loadSeqRef = useRef(0);

    // Turns a failed response into a message that says what actually went wrong. Without this a 403 or
    // 500 leaves `definitions` empty and the grid renders "No test definitions were found" — which sent
    // us looking at the database when the real problem was authorization.
    const describeFailure = (response, what) => {
        if (!response) return `Could not reach the API while loading ${what}.`;
        if (response.status === 401) return `Not signed in (401) while loading ${what}. Try signing in again.`;
        if (response.status === 403) return `Not authorized (403) to load ${what}. This tab requires the "admin" scope on your access token.`;
        return `Loading ${what} failed (HTTP ${response.status}).`;
    };

    const loadAll = useCallback(async () => {
        const seq = ++loadSeqRef.current;
        try {
            const [definitionsResponse, inFlightResponse, statusResponse] = await Promise.all([
                apiGet('a_regression/TestDefinitions?includeItems=true', getAccessTokenSilently),
                apiGet('a_regression/InFlightRuns', getAccessTokenSilently),
                apiGet('a_regression/RunStatus', getAccessTokenSilently)
            ]);

            // Read all three bodies first, then apply them together, and only if no newer load has started.
            const definitionsData = definitionsResponse && definitionsResponse.ok ? await definitionsResponse.json() : null;
            const inFlightData = inFlightResponse && inFlightResponse.ok ? await inFlightResponse.json() : [];
            const statusData = statusResponse && statusResponse.ok ? await statusResponse.json() : { isRunning: false };
            if (seq !== loadSeqRef.current) return;

            // The definitions call drives the whole grid, so its failure is the one worth reporting.
            if (definitionsData) {
                setDefinitions(definitionsData);
                setLoadError(null);
            } else {
                setDefinitions([]);
                setLoadError(describeFailure(definitionsResponse, 'the regression tests'));
            }
            setInFlightRuns(inFlightData);
            setRunStatus(statusData);
        } catch (error) {
            console.log(error);
            if (seq === loadSeqRef.current) {
                setLoadError('Could not load regression test data. Check that the API is running, then refresh.');
            }
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // Numbered like loadAll, so a slow older response cannot overwrite a newer one (e.g. the mount-time check
    // landing after the re-check that follows an update).
    const definitionsStatusSeqRef = useRef(0);
    const loadDefinitionsStatus = useCallback(async () => {
        const seq = ++definitionsStatusSeqRef.current;
        try {
            const response = await apiGet('a_regression/TestDefinitionsStatus', getAccessTokenSilently);
            const data = response && response.ok ? await response.json() : null;
            if (seq === definitionsStatusSeqRef.current) setDefinitionsStatus(data);
        } catch (error) {
            console.log(error);
            if (seq === definitionsStatusSeqRef.current) setDefinitionsStatus(null);
        }
    }, [getAccessTokenSilently]);

    // Checked on load, whenever the definitions change (someone pressed Update, or ran the script by hand), and
    // when the tab is looked at again - a tab left open across an API deploy with a new script would otherwise
    // keep its old answer. Not on the run poll: every check is a full (rolled-back) dry run of the script.
    useEffect(() => {
        loadDefinitionsStatus();
    }, [loadDefinitionsStatus, runStatus.definitionsUpdatedAt]);

    useEffect(() => {
        const recheck = () => { if (document.visibilityState === 'visible') loadDefinitionsStatus(); };
        document.addEventListener('visibilitychange', recheck);
        window.addEventListener('focus', recheck);
        return () => {
            document.removeEventListener('visibilitychange', recheck);
            window.removeEventListener('focus', recheck);
        };
    }, [loadDefinitionsStatus]);

    // The config check is a handful of reads, so it runs on load and whenever the tab is looked at again (someone
    // may have fixed an account in the meantime), never on the run poll.
    const configCheckSeqRef = useRef(0);
    const loadConfigCheck = useCallback(async () => {
        const seq = ++configCheckSeqRef.current;
        try {
            const response = await apiGet('a_regression/ConfigCheck', getAccessTokenSilently);
            const data = response && response.ok ? await response.json() : null;
            if (seq === configCheckSeqRef.current) setConfigCheck(data);
        } catch (error) {
            console.log(error);
            if (seq === configCheckSeqRef.current) setConfigCheck(null);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        loadConfigCheck();
        const recheck = () => { if (document.visibilityState === 'visible') loadConfigCheck(); };
        document.addEventListener('visibilitychange', recheck);
        window.addEventListener('focus', recheck);
        return () => {
            document.removeEventListener('visibilitychange', recheck);
            window.removeEventListener('focus', recheck);
        };
    }, [loadConfigCheck]);

    const runHistorySeqRef = useRef(0);
    const loadRunHistory = useCallback(async () => {
        const seq = ++runHistorySeqRef.current;
        try {
            const response = await apiGet(`a_regression/RunHistory?take=${RUN_HISTORY_TAKE}`, getAccessTokenSilently);
            if (!response || !response.ok) return;
            const runs = await response.json();
            if (seq !== runHistorySeqRef.current) return;
            const byDefinition = {};
            (runs || []).forEach(run => { (byDefinition[run.testDefinitionId] = byDefinition[run.testDefinitionId] || []).push(run); });
            Object.values(byDefinition).forEach(list => list.sort((a, b) => b.id - a.id));
            setRunHistory(byDefinition);
        } catch (error) {
            console.log(error);
        }
    }, [getAccessTokenSilently]);

    // Reloaded when a test's latest run changes or finishes - not on every poll, which would refetch every
    // test's history every 4 seconds for no change.
    const latestRunsKey = definitions
        .map(d => `${d.id}:${d.latestRun?.id ?? ''}:${d.latestRun?.isComplete ? 1 : 0}`)
        .join(',');
    useEffect(() => {
        loadRunHistory();
    }, [loadRunHistory, latestRunsKey]);

    useEffect(() => {
        const timer = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);

    // Poll only while there is something to watch: either a Process 14 row is Requested/Processing,
    // or individual runs are still short of a terminal status (a run can outlive its driving process
    // if that process failed part-way).
    const shouldPoll = runStatus.isRunning || inFlightRuns.length > 0
        || (runStatus.runningTestDefinitionIds || []).length > 0 || Object.keys(pendingLaunches).length > 0;
    isPollingActiveRef.current = shouldPoll;

    useEffect(() => {
        if (!shouldPoll) return undefined;

        const timer = setInterval(() => {
            if (isPollingActiveRef.current) loadAll();
        }, POLL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [shouldPoll, loadAll]);

    // Action banners ("Started 1 test(s).", "Cancelling…") are transient confirmations, so they clear
    // themselves after BANNER_TIMEOUT_MS rather than sitting there until dismissed. loadError is
    // deliberately NOT auto-dismissed — it reports a broken state that is still broken.
    useEffect(() => {
        if (!banner) return undefined;

        const timer = setTimeout(() => setBanner(null), BANNER_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [banner]);

    const launchTests = async (testDefinitionIds, label) => {
        if (!testDefinitionIds || testDefinitionIds.length === 0) {
            setBanner({ variant: 'warning', message: `There are no ${label} to run.` });
            return;
        }

        if (launchInFlightRef.current) return;
        launchInFlightRef.current = true;
        setLaunching(true);
        setBanner(null);

        const clickedAt = Date.now();
        setPendingLaunches(previous => {
            const next = { ...previous };
            testDefinitionIds.forEach(id => { next[id] = clickedAt; });
            return next;
        });
        const dropPending = (ids) => setPendingLaunches(previous => {
            const next = { ...previous };
            ids.forEach(id => { delete next[id]; });
            return next;
        });

        try {
            const response = await apiPost(
                'a_regression/Run',
                JSON.stringify({ testDefinitionIds }),
                true,
                getAccessTokenSilently
            );

            if (!response || !response.ok) {
                dropPending(testDefinitionIds);
                setBanner({ variant: 'danger', message: 'The run could not be started. Check the API logs.' });
                return;
            }

            const result = await response.json();
            setBanner({ variant: result.success ? 'success' : 'warning', message: result.message });

            // Keep only the tests the server actually queued; the rest were skipped (already running,
            // or no longer exist) and the server's own running list covers the running ones.
            const queuedIds = new Set(result.success ? (result.testDefinitionIds || []) : []);
            dropPending(testDefinitionIds.filter(id => !queuedIds.has(id)));

            await loadAll();
        } catch (error) {
            console.log(error);
            dropPending(testDefinitionIds);
            setBanner({ variant: 'danger', message: 'The run could not be started.' });
        } finally {
            launchInFlightRef.current = false;
            setLaunching(false);
        }
    };

    // Downloads one file's diff log. Returns null on success, or what went wrong: its only caller is the diff viewer,
    // which shows the message itself (the page banner would be hidden behind the dialog).
    const downloadDiffLog = async (item) => {
        try {
            const response = await apiGet(`a_regression/DiffLog?testItemId=${item.id}`, getAccessTokenSilently);

            if (!response || !response.ok) {
                return `No diff log is available for "${item.fileName}".`;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${(item.fileName || 'test').replace(/\.[^.]+$/, '')}_DiffLog.txt`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return null;
        } catch (error) {
            console.log(error);
            return 'The diff log could not be downloaded.';
        }
    };

    // One zip with the diff log of every file that failed verification today, a folder per test and run.
    // "Today" is the viewer's own day: their local midnight is sent to the API as a UTC instant.
    const downloadTodaysDiffLogs = async () => {
        setDownloadingDiffLogs(true);
        setBanner(null);

        try {
            const now = new Date();
            const localMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const response = await apiGet(`a_regression/DiffLogsZip?since=${encodeURIComponent(localMidnight.toISOString())}`, getAccessTokenSilently);

            if (response && response.status === 404) {
                setBanner({ variant: 'info', message: 'No tests run today have diff logs.' });
                return;
            }
            if (!response || !response.ok) {
                setBanner({ variant: 'danger', message: 'The diff logs could not be downloaded.' });
                return;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const pad = (n) => String(n).padStart(2, '0');
            link.href = url;
            link.setAttribute('download', `Regression diff logs ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.log(error);
            setBanner({ variant: 'danger', message: 'The diff logs could not be downloaded.' });
        } finally {
            setDownloadingDiffLogs(false);
        }
    };

    useEffect(() => {
        const confirmed = new Set([...inFlightRuns.map(run => run.testDefinitionId), ...(runStatus.runningTestDefinitionIds || [])]);
        const now = Date.now();
        setPendingLaunches(previous => {
            const stale = Object.keys(previous).filter(id => confirmed.has(Number(id)) || now - previous[id] > 60000);
            if (stale.length === 0) return previous;
            const next = { ...previous };
            stale.forEach(id => { delete next[id]; });
            return next;
        });
    }, [inFlightRuns, runStatus]);

    useEffect(() => {
        if (!runStatus.isRunning || !runStatus.processId) return undefined;
        if (lastNoticedProcessId.current === runStatus.processId) return undefined;
        lastNoticedProcessId.current = runStatus.processId;
        setRunNoticeVisible(true);
        const timer = setTimeout(() => setRunNoticeVisible(false), RUN_NOTICE_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [runStatus.isRunning, runStatus.processId]);

    const cancelTests = async () => {
        setCancelling(true);
        setBanner(null);

        try {
            const response = await apiPost('a_regression/Cancel', JSON.stringify({}), true, getAccessTokenSilently);

            if (!response || !response.ok) {
                setBanner({ variant: 'danger', message: 'The run could not be cancelled. Check the API logs.' });
                return;
            }

            const result = await response.json();
            setBanner({ variant: result.success ? 'warning' : 'info', message: result.message });
            await loadAll();
        } catch (error) {
            console.log(error);
            setBanner({ variant: 'danger', message: 'The run could not be cancelled.' });
        } finally {
            setCancelling(false);
        }
    };

    // At most one test is expanded at a time — a bulk test's file list runs to 39 rows, so two open at
    // once pushes the rest of the grid off screen. Opening one closes whatever was open.
    const updateTestDefinitions = async () => {
        setUpdatingDefinitions(true);
        setBanner(null);

        try {
            const response = await apiPost('a_regression/UpdateTestDefinitions', JSON.stringify({}), true, getAccessTokenSilently);

            if (!response || !response.ok) {
                setBanner({ variant: 'danger', message: 'The test definitions could not be updated. Check the API logs.' });
                return;
            }

            const result = await response.json();
            setBanner({ variant: result.success ? 'success' : 'warning', message: result.message });

            // Reload so the grid shows the refreshed descriptions / categories / file counts, and re-check
            // whether anything is still out of step with the script.
            if (result.success) await loadAll();
            await loadDefinitionsStatus();
        } catch (error) {
            console.log(error);
            setBanner({ variant: 'danger', message: 'The test definitions could not be updated.' });
        } finally {
            setUpdatingDefinitions(false);
        }
    };

    const toggleExpanded = (definitionId) => {
        setExpandedId(previous => (previous === definitionId ? null : definitionId));
    };

    // Legacy: OGSYS PCX output is retired, so those definitions are not runnable and are listed
    // separately below the main grid rather than mixed in with live tests.
    const isLegacyDefinition = (definition) => (definition.categoryName || '').toUpperCase().includes('OGSYS');

    // Which definitions are ACTUALLY running now. This must come from InFlightRuns, not from
    // "latestRun isn't complete": a run only leaves Processing/Verifying when its last TestItem
    // finishes, so a run abandoned part-way (crashed WebJob, poisoned message) stays non-terminal in
    // the table forever. Deriving from latestRun made those corpses render as running — spinner,
    // auto-expanded, launch disabled — which is what made an old bulk run look like it had just
    // started. InFlightRuns is bounded to the last 24h server-side precisely to exclude them.
    //
    // Plus two sources that cover a test which has been launched but has no run row yet (the worker creates
    // it only when it reaches that test): the server's RunningTestDefinitionIds, which includes tests
    // still queued in a live run, and the tests clicked in this tab that the server has not reported yet.
    // Without them a launched test briefly looked idle and could be launched a second time.
    const serverBusyIds = [...inFlightRuns.map(run => run.testDefinitionId), ...(runStatus.runningTestDefinitionIds || [])];
    const runningDefinitionIds = new Set([...serverBusyIds, ...Object.keys(pendingLaunches).map(Number)]);

    // Every file currently mid-flight, across all definitions.
    const activeItems = inFlightRuns.flatMap(run => (run.items || []).filter(item => !item.isComplete));

    // "7 of 10 file slots in use". The count is the one the workers' admission gate uses (all runs share one
    // window); the limit is what each running worker stamped on its run. Workers on different settings can
    // disagree, so then every limit is listed. An older API sends neither, so fall back to the file count.
    const activeFileCount = typeof runStatus.activeFileCount === 'number' ? runStatus.activeFileCount : activeItems.length;
    const batchSizes = runStatus.effectiveBatchSizes || [];
    const slotSummary = batchSizes.length === 1
        ? `${activeFileCount} of ${batchSizes[0]} file slot${batchSizes[0] === 1 ? '' : 's'} in use`
        : batchSizes.length > 1
            ? `${activeFileCount} file${activeFileCount === 1 ? '' : 's'} in progress (worker limits differ: ${batchSizes.join(', ')})`
            : `${activeFileCount} file${activeFileCount === 1 ? '' : 's'} in progress`;

    // Concurrency is PER TEST, not global (2026-08-27). Runs may overlap — each creates its own
    // statements and stages inputs under statement/{id}/request, so they cannot collide. What must not
    // overlap is one definition running twice at once, so only THAT test's controls are disabled. A
    // stalled run releases its test again, which is what makes a timed-out test re-runnable.

    // Global actions are only blocked while a request is mid-flight, to stop double-submits.
    // updatingDefinitions blocks EVERY launch: the script rewrites the per-test file lists, so a run
    // started mid-update could read half the old catalogue and half the new one.
    const launchBusy = launching || cancelling || updatingDefinitions;
    const canCancel = runStatus.isRunning && !runStatus.isCancelling && !cancelling;

    // Accidental re-run guard for the definitions script. The remaining seconds are computed by the
    // API off the TestDefinition temporal stamp and refresh on every poll, so this stays correct
    // across a page reload and across two people looking at the tab at once — which a local timer
    // would not. The API enforces the same window; this only keeps the button honest.
    const cooldownRemaining = runStatus.definitionsUpdateSecondsRemaining || 0;
    const inUpdateCooldown = cooldownRemaining > 0;

    // Nothing to update: the dry run found that the script would change no test definition.
    const definitionsUpToDate = definitionsStatus?.upToDate === true;
    const definitionsDiffer = !definitionsUpToDate && (definitionsStatus?.differingDefinitionIds?.length || 0) > 0;
    const updateDefinitionsDisabled = updatingDefinitions || launching || cancelling || runStatus.isRunning
        || inUpdateCooldown || definitionsUpToDate;

    // The bulk buttons launch whatever is not already running (and never the retired OGSYS tests).
    const runnableDefinitions = definitions.filter(d => !isLegacyDefinition(d) && !runningDefinitionIds.has(d.id));
    const allIds = runnableDefinitions.map(d => d.id);
    const bulkIds = runnableDefinitions.filter(d => d.scope === 'Bulk').map(d => d.id);
    const singleIds = runnableDefinitions.filter(d => d.scope === 'Single').map(d => d.id);
    // Smoke test: the one quick post-deploy check that the backend is up (Dave, 2026-10-20).
    const smokeIds = runnableDefinitions
        .filter(d => (d.testDescription || '').trim().toLowerCase() === '[single test] basic -> xls')
        .map(d => d.id);

    // Rows start collapsed, running ones included — a run auto-expanding several rows pushed the rest
    // of the grid off screen. Expansion is purely manual.
    const isRowExpanded = (definition) => expandedId === definition.id;

    // Running tests are lifted into their own table above the list so the run is separated from the
    // catalogue; each drops back into the main list, carrying its result, once its run finishes.
    // Most recently updated first, so whatever you just ran is at the top. Tests that have never run
    // sort last rather than jumping to the top on a null date.
    const byLastUpdatedDesc = (a, b) => {
        const aDate = a.latestRun?.statusDate ? new Date(a.latestRun.statusDate).getTime() : -Infinity;
        const bDate = b.latestRun?.statusDate ? new Date(b.latestRun.statusDate).getTime() : -Infinity;
        if (aDate === bDate) return a.id - b.id;
        return bDate - aDate;
    };

    // The run a row displays: a running test takes its run from the live in-flight list, any other test its
    // definition's latest run - the same rule renderDefinitionRows uses.
    const displayedRun = (definition, isRunning) => isRunning
        ? (inFlightRuns.find(run => run.testDefinitionId === definition.id) || null)
        : definition.latestRun;

    // Text of the Last Run / Status cell (mirrors renderDefinitionRows), so that column sorts by what it shows.
    const lastRunLabel = (definition, isRunning) => {
        const run = displayedRun(definition, isRunning);
        if (isRunning) return run ? RUNNING_STYLE.label : 'Queued';
        if (!run) return 'Never run';
        return run.wasCancelled ? 'Cancelled' : run.isStalled ? 'Stalled' : (run.statusName || '');
    };

    // Sortable columns. null values (e.g. a test that has never run has no Last Updated) always sort last.
    const sortValues = {
        test: (d) => (d.testDescription || '').toLowerCase(),
        scope: (d) => (d.scope || '').toLowerCase(),
        category: (d) => (d.categoryName || '').toLowerCase(),
        files: (d) => d.fileCount || 0,
        lastRun: (d, isRunning) => lastRunLabel(d, isRunning).toLowerCase(),
        lastUpdated: (d, isRunning) => parseApiDate(displayedRun(d, isRunning)?.statusDate)?.getTime() ?? null,
    };

    // Default order of every section: most recently updated first.
    const DEFAULT_SORT = { column: 'lastUpdated', direction: 'desc' };

    const sortSection = (list, section, isRunning) => {
        const sort = sortBy[section];
        if (!sort) return [...list].sort(byLastUpdatedDesc);
        const valueOf = sortValues[sort.column];
        const direction = sort.direction === 'asc' ? 1 : -1;
        return [...list].sort((a, b) => {
            const va = valueOf(a, isRunning);
            const vb = valueOf(b, isRunning);
            if (va === null || vb === null) {
                if (va === vb) return a.id - b.id;
                return va === null ? 1 : -1;
            }
            if (va < vb) return -direction;
            if (va > vb) return direction;
            return a.id - b.id;
        });
    };

    // Click a header to sort by it (ascending first); click it again to reverse.
    const toggleSort = (section, column) => setSortBy(previous => {
        const current = previous[section] || DEFAULT_SORT;
        const next = current.column !== column
            ? { column, direction: 'asc' }
            : { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
        return { ...previous, [section]: next };
    });

    const sortableHeader = (section, column, label) => {
        const sort = sortBy[section] || DEFAULT_SORT;
        const active = sort.column === column;
        return (
            <th
                style={{ ...adminStickyThStyle, whiteSpace: 'nowrap' }}
                aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
                <button
                    type="button"
                    className="btn btn-link p-0"
                    style={{ color: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', textDecoration: 'none', boxShadow: 'none' }}
                    onClick={() => toggleSort(section, column)}
                    title={`Sort by ${label}`}
                >
                    {label}
                    <FontAwesomeIcon
                        icon={active ? (sort.direction === 'asc' ? faSortUp : faSortDown) : faSort}
                        style={{ marginLeft: 5, width: '9px', color: active ? COLORS.black : '#c4c4c4' }}
                    />
                </button>
            </th>
        );
    };

    // Same columns in all four sections; only the Last Run column is called Status while a test runs.
    const sectionHeaderRow = (section, lastRunColumnLabel) => (
        <tr>
            <th style={{ ...adminStickyThStyle, width: '46px' }}></th>
            <th style={{ ...adminStickyThStyle, width: '46px' }}></th>
            {sortableHeader(section, 'test', 'Test')}
            {sortableHeader(section, 'scope', 'Scope')}
            {sortableHeader(section, 'category', 'Category')}
            {sortableHeader(section, 'files', 'Files')}
            {sortableHeader(section, 'lastRun', lastRunColumnLabel)}
            <th style={adminStickyThStyle}>Progress</th>
            {sortableHeader(section, 'lastUpdated', 'Last Updated')}
        </tr>
    );

    const runningDefinitions = sortSection(definitions.filter(d => runningDefinitionIds.has(d.id)), 'running', true);

    // Finished (Success, Fail or Cancelled - not Stalled) within the last RECENTLY_COMPLETED_MINUTES: shown in
    // their own card between Running now and the Test List, then back in the list once that time is up.
    const isRecentlyCompleted = (definition) => {
        const run = definition.latestRun;
        if (!run || !run.isComplete || run.isStalled) return false;
        const finished = parseApiDate(run.statusDate);
        return !!finished && nowTick - finished.getTime() <= RECENTLY_COMPLETED_MINUTES * 60000;
    };
    const recentlyCompletedDefinitions = sortSection(
        definitions.filter(d => !runningDefinitionIds.has(d.id) && !isLegacyDefinition(d) && isRecentlyCompleted(d)),
        'recent', false);
    const recentlyCompletedIds = new Set(recentlyCompletedDefinitions.map(d => d.id));

    // Output format, derived from the category name (BASIC-EL_XLS, GRID-CDEX, BASIC-OGSYS_PCX_TXT, …).
    // CDEX and OGSYS are checked before XLS because a name can contain more than one token.
    const getFormat = (definition) => {
        const name = (definition.categoryName || '').toUpperCase();
        if (name.includes('CDEX')) return 'CDEX';
        if (name.includes('OGSYS')) return 'OgsysPcxTxt';
        if (name.includes('XLS')) return 'XLS';
        return 'Other';
    };

    // Parser family, also from the category name (BASIC-*, GRID-*, JIB-*).
    const getFamily = (definition) => {
        const name = (definition.categoryName || '').toUpperCase();
        if (name.startsWith('JIB')) return 'JIB';
        if (name.startsWith('GRID')) return 'Grid';
        if (name.startsWith('BASIC')) return 'Basic';
        return 'Other';
    };

    // Filters are OR within a group and AND across groups, so "Single + Grid + CDEX" means the
    // single-scope Grid CDEX test. An empty group means "no constraint", not "match nothing".
    const scopeFilters = activeFilters.filter(f => f === 'Single' || f === 'Bulk');
    const familyFilters = activeFilters.filter(f => f === 'Basic' || f === 'Grid' || f === 'JIB');
    const formatFilters = activeFilters.filter(f => f === 'CDEX' || f === 'XLS' || f === 'OgsysPcxTxt');

    const matchesFilters = (definition) =>
        (scopeFilters.length === 0 || scopeFilters.includes(definition.scope))
        && (familyFilters.length === 0 || familyFilters.includes(getFamily(definition)))
        && (formatFilters.length === 0 || formatFilters.includes(getFormat(definition)));

    // The Test List: everything not running, not recently completed and not legacy.
    const listDefinitions = definitions
        .filter(d => !runningDefinitionIds.has(d.id))
        .filter(d => !recentlyCompletedIds.has(d.id))
        .filter(d => !isLegacyDefinition(d));
    const idleDefinitions = sortSection(listDefinitions.filter(matchesFilters), 'list', false);

    const legacyDefinitions = sortSection(definitions.filter(d => isLegacyDefinition(d) && !runningDefinitionIds.has(d.id)), 'legacy', false);

    const toggleFilter = (filter) => {
        setActiveFilters(previous => previous.includes(filter)
            ? previous.filter(f => f !== filter)
            : [...previous, filter]);
    };

    // The files a run will get to but hasn't started yet. TestItems are only created as files are
    // admitted into the batch window, so a not-yet-started file has NO row of its own — without this
    // the dropdown of a running 37-file test would show only the handful in flight and look truncated.
    // Matched by name against the definition's own file list.
    const buildFileRows = (definition, run, isRunning) => {
        const items = run?.items || [];
        const itemsByFile = new Map(items.map(item => [item.fileName, item]));

        const rows = (definition.fileNames || []).map(fileName => {
            const item = itemsByFile.get(fileName);
            if (item) return { key: `item-${item.id}`, item, fileName };

            // No TestItem yet: queued if the run is still going, otherwise it never ran (the run was
            // cancelled, or failed before reaching this file).
            return { key: `pending-${fileName}`, item: null, fileName, pendingLabel: isRunning ? 'Queued' : 'Not run' };
        });

        // Defensive: an item whose file is no longer in the definition's metadata (the list was edited
        // after the run) would otherwise vanish from its own run's detail.
        const knownFiles = new Set(definition.fileNames || []);
        items.filter(item => !knownFiles.has(item.fileName))
            .forEach(item => rows.push({ key: `item-${item.id}`, item, fileName: item.fileName }));

        return rows;
    };

    // One row renderer for BOTH tables so a test looks and behaves identically whether it is running
    // (top table) or idle (main list). `isRunning` only changes the leading cell (spinner vs play) and
    // how not-yet-started files are labelled.
    const renderDefinitionRows = (definition, isRunning) => {
        const isExpanded = isRowExpanded(definition);
        const isLegacy = isLegacyDefinition(definition);
        // A queued test (launched, but its run has not reached it yet) has no run of its own: its
        // definition.latestRun is still the PREVIOUS run, so show it as Queued rather than that old result.
        // A running test takes its run from the live in-flight list (the definitions list can lag a poll
        // behind and still hold the previous, finished run).
        const liveRun = isRunning ? inFlightRuns.find(run => run.testDefinitionId === definition.id) : null;
        const isQueued = isRunning && !liveRun;
        const latestRun = isRunning ? (liveRun || null) : definition.latestRun;
        const fileRows = buildFileRows(definition, latestRun, isRunning);

        // Denominator differs by context. While RUNNING, itemCount only counts TestItems created so
        // far, so it would read "3/3 done" with 34 files still queued — the definition's file count is
        // the real target. For a FINISHED run, itemCount is what that run actually covered; using the
        // current file count would retroactively mark a complete run short if files were added since.
        const progressTotal = isRunning
            ? (definition.fileCount || latestRun?.itemCount || 0)
            : (latestRun?.itemCount || 0);

        // The history is only refetched when a run starts or finishes, so the live run's entry takes its counts from
        // the live poll instead of keeping the ones it had when it was created.
        const history = (runHistory[definition.id] || []).map(run => (liveRun && run.id === liveRun.id ? { ...run, ...liveRun } : run));

        // Time so far and a rough time left for a running test, from how long its recent full runs took. Only a
        // guide: a run shares the file slots with every other run, so a busy queue stretches it. Timed from the first
        // admitted file (from the run's own files on an older API without firstItemStartDate): until then the run is
        // only waiting behind older runs for a slot.
        const runStarted = liveRun
            ? (parseApiDate(liveRun.firstItemStartDate)
                || (liveRun.items || []).map(i => parseApiDate(i.startDate)).filter(Boolean).sort((a, b) => a - b)[0]
                || null)
            : null;
        const elapsedMs = runStarted ? Math.max(0, Date.now() - runStarted.getTime()) : null;
        const typicalMs = isRunning ? typicalDurationMs(history.filter(run => run.id !== liveRun?.id), definition.fileCount) : null;
        let timing = null;
        let timingTitle = '';
        if (liveRun && !runStarted) {
            timing = 'waiting for a file slot';
            timingTitle = 'No file of this run has been admitted yet: older runs are using the shared file slots.';
        } else if (elapsedMs != null) {
            timing = typicalMs == null
                ? `${formatDuration(elapsedMs)} elapsed`
                : elapsedMs < typicalMs
                    ? `${formatDuration(elapsedMs)} elapsed · ~${formatDuration(typicalMs - elapsedMs)} left`
                    : `${formatDuration(elapsedMs)} elapsed · longer than usual (~${formatDuration(typicalMs)})`;
            timingTitle = typicalMs == null
                ? 'No earlier full run of this test to estimate from yet.'
                : `A full run of this test usually takes about ${formatDuration(typicalMs)} (median of its recent completed runs). Runs share the file slots, so a busy queue takes longer.`;
        }

        return (
            <React.Fragment key={definition.id}>
                <tr>
                    <td>
                        {isRunning ? (
                            // A spinner in place of the play button: the row-level status pill alone
                            // doesn't convey at a glance which test is the one working.
                            <div
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '31px' }}
                                title={`"${definition.testDescription}" is running`}
                            >
                                <Spinner animation="border" size="sm" style={{ color: COLORS.gold }} />
                            </div>
                        ) : (
                            <Button
                                size="sm"
                                title={isLegacy
                                    ? 'Legacy test — OGSYS PCX output is retired'
                                    : `Run "${definition.testDescription}"`}
                                style={{ ...btnGold, padding: '2px 8px' }}
                                disabled={launchBusy || isLegacy}
                                onClick={() => launchTests([definition.id], 'tests')}
                            >
                                <FontAwesomeIcon icon={faPlay} style={{ width: '11px' }} />
                            </Button>
                        )}
                    </td>
                    <td>
                        <Button
                            variant="link"
                            size="sm"
                            title={isExpanded ? 'Hide files' : 'Show files'}
                            style={{ padding: 0, color: '#6b7280' }}
                            onClick={() => toggleExpanded(definition.id)}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                        </Button>
                    </td>
                    <td>
                        <div style={{ fontWeight: 600, color: COLORS.black }}>{definition.testDescription}</div>
                        <div style={{ fontSize: '0.76rem', color: '#9ca3af' }}>
                            #{definition.id} · Account {definition.accountId}
                            {!definition.isActive && ' · inactive'}
                        </div>
                    </td>
                    <td>{definition.scope}</td>
                    <td style={{ color: '#6b7280' }}>{definition.categoryName}</td>
                    <td>{definition.fileCount}</td>
                    <td>
                        {isRunning ? (
                            <span
                                style={{ ...statusLabel, backgroundColor: isQueued ? '#e5e7eb' : RUNNING_STYLE.bg, color: isQueued ? '#4b5563' : RUNNING_STYLE.fg, fontWeight: 600, fontSize: '0.78rem' }}
                                title={isQueued
                                    ? 'Launched and waiting for its turn in the run; it cannot be started again until it finishes.'
                                    : `Run #${latestRun?.id ?? '—'} is still in progress; the run's own status only becomes final when every file has finished.`}
                            >
                                {isQueued ? 'Queued' : RUNNING_STYLE.label}
                                {latestRun?.itemsFailed > 0 && ` · ${latestRun.itemsFailed} failed so far`}
                            </span>
                        ) : latestRun
                            ? <StatusPill
                                statusId={latestRun.statusId}
                                // A cancelled run lands on the Fail status (the TestStatus lookup has no
                                // Cancelled row), so relabel it rather than reporting a failure that
                                // never actually happened.
                                statusName={latestRun.wasCancelled
                                    ? 'Cancelled'
                                    : latestRun.isStalled ? 'Stalled' : latestRun.statusName} />
                            : <span style={{ color: '#9ca3af' }}>Never run</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                        {isQueued
                            ? <span style={{ color: '#6b7280' }}>Waiting to start</span>
                            : latestRun
                                ? `${latestRun.itemsCompleted}/${progressTotal} Completed · ${latestRun.itemsSucceeded} Passed · ${latestRun.itemsFailed} Failed`
                                : '—'}
                        {timing && (
                            <div style={{ fontSize: '0.74rem', color: '#9ca3af' }} title={timingTitle}>
                                {timing}
                            </div>
                        )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280' }}>
                        {formatDateTime(latestRun?.statusDate)}
                    </td>
                </tr>

                {isExpanded && (
                    <tr>
                        <td colSpan={9} style={{ background: '#fafafa' }}>
                            <div style={{ padding: '4px 0' }}>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {isRunning && <Spinner animation="border" size="sm" style={{ color: COLORS.gold }} />}
                                    <span>
                                        {latestRun
                                            ? <>
                                                {isRunning ? 'Current run' : 'Run'} #{latestRun.id}
                                                {' · started '}{formatDateTime(latestRun.startDate)}
                                                {' · '}{latestRun.itemsCompleted}/{progressTotal} done
                                                {latestRun.statusNotes ? ` · ${latestRun.statusNotes}` : ''}
                                            </>
                                            : isQueued
                                                ? <>Queued — waiting for the run to reach this test · {definition.fileCount} source file{definition.fileCount === 1 ? '' : 's'}.</>
                                                : <>Never run — {definition.fileCount} source file{definition.fileCount === 1 ? '' : 's'}.</>}
                                    </span>
                                </div>

                                {/* The test's recent runs, newest first: result, when, how long, and pass/fail counts. */}
                                {history.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', fontSize: '0.74rem', marginBottom: '8px' }}>
                                        <span style={{ color: '#6b7280', fontWeight: 600 }}>Recent runs:</span>
                                        {history.map(run => {
                                            const label = run.wasCancelled ? 'Cancelled' : run.isStalled ? 'Stalled' : !run.isComplete ? 'Running' : run.statusName;
                                            const style = run.wasCancelled
                                                ? { bg: '#e5e7eb', fg: '#4b5563' }
                                                : run.isStalled
                                                    ? { bg: '#fde68a', fg: '#7a5a12' }
                                                    : !run.isComplete
                                                        ? RUNNING_STYLE
                                                        : (STATUS_STYLES[run.statusId] || { bg: '#e5e7eb', fg: '#4b5563' });
                                            const duration = run.isComplete ? runDurationMs(run) : null;
                                            return (
                                                <span
                                                    key={run.id}
                                                    title={`Run #${run.id} · ${label} · started ${formatDateTime(run.startDate)}`
                                                        + `${duration != null ? ` · took ${formatDuration(duration)}` : ''}`
                                                        + ` · ${run.itemsSucceeded} passed, ${run.itemsFailed} failed of ${run.itemCount}`}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '1px 8px',
                                                        borderRadius: '999px', background: '#fff', border: `1px solid ${style.bg}`,
                                                        outline: run.id === latestRun?.id ? `1px solid ${style.bg}` : 'none'
                                                    }}
                                                >
                                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: style.bg }} />
                                                    <span style={{ color: '#374151' }}>{formatShortDateTime(run.startDate)}</span>
                                                    {duration != null && <span style={{ color: '#9ca3af' }}>{formatDuration(duration)}</span>}
                                                    <span style={{ color: run.itemsFailed > 0 ? '#921E1E' : '#1B602D' }}>
                                                        {run.itemsSucceeded}/{run.itemCount}
                                                    </span>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}

                                <Table size="sm" style={{ fontSize: '0.8rem', marginBottom: 0, background: 'transparent' }}>
                                    <thead>
                                        <tr>
                                            <th>File</th>
                                            <th>Status</th>
                                            <th>Statement</th>
                                            <th>Updated</th>
                                            <th>Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fileRows.map(({ key, item, fileName, pendingLabel }) => (
                                            <tr key={key}>
                                                <td style={{ maxWidth: '320px', wordBreak: 'break-word' }}>{fileName}</td>
                                                <td>
                                                    {item
                                                        ? <StatusPill statusId={item.statusId} statusName={item.statusName} />
                                                        : <span style={{ ...statusLabel, backgroundColor: '#e5e7eb', color: '#4b5563', fontWeight: 600, fontSize: '0.78rem' }}>{pendingLabel}</span>}
                                                </td>
                                                <td>{item?.statementId ?? '—'}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{item ? formatDateTime(item.statusDate) : '—'}</td>
                                                <td style={{ maxWidth: '420px', wordBreak: 'break-word', color: '#6b7280' }}>
                                                    {item ? getItemMessage(item) : ''}
                                                    {item?.hasDiffLog && (
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            style={{ padding: '0 0 0 6px', fontSize: '0.78rem', color: COLORS.blue }}
                                                            title="Show what differs from the control file"
                                                            onClick={() => setDiffViewerItem(item)}
                                                        >
                                                            view diff
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {fileRows.length === 0 && (
                                            <tr>
                                                <td colSpan={5} style={{ color: '#9ca3af' }}>No files are configured for this test.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                <Spinner animation="border" style={{ color: COLORS.gold }} />
            </div>
        );
    }

    return (
        <div>
            {/* ---------- Header ---------- */}
            <Row className="align-items-center" style={{ marginBottom: '10px' }}>
                <Col xs={12} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                        width: '38px', height: '38px', borderRadius: '9px', background: '#f3f4f6',
                        border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', flexShrink: 0
                    }}>
                        <FontAwesomeIcon icon={faFlask} style={{ width: '16px', color: COLORS.gold }} />
                    </div>
                    <div>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: COLORS.black }}>Document Conversion Regression Tests</div>
                        <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                            Each test pushes its PDFs through the live conversion pipeline, then verifies the
                            regenerated export against the stored control output.
                        </div>
                    </div>

                    {/* Whether this environment's ZZTEST accounts still match the settings the control files were made
                        with. A mismatch fails tests for reasons that have nothing to do with the code under test. */}
                    {configCheck && (
                        <Button
                            size="sm"
                            variant={configCheck.ok ? 'outline-success' : 'outline-danger'}
                            style={{ marginLeft: 'auto', fontWeight: 600, whiteSpace: 'nowrap' }}
                            title={configCheck.ok
                                ? `All ${configCheck.checks?.length ?? 0} ZZTEST account checks pass in ${configCheck.environment}. Click for details.`
                                : `${configCheck.failures} ZZTEST account check${configCheck.failures === 1 ? '' : 's'} fail in ${configCheck.environment} - tests may fail for configuration reasons. Click for details.`}
                            onClick={() => setShowConfigDetails(show => !show)}
                        >
                            <FontAwesomeIcon icon={configCheck.ok ? faCircleCheck : faTriangleExclamation} style={{ marginRight: 6 }} />
                            {configCheck.ok
                                ? `Test accounts OK (${configCheck.environment})`
                                : `${configCheck.failures} test account issue${configCheck.failures === 1 ? '' : 's'} (${configCheck.environment})`}
                        </Button>
                    )}
                </Col>
            </Row>

            {configCheck && showConfigDetails && (
                <Alert
                    variant={configCheck.ok ? 'success' : 'warning'}
                    dismissible
                    onClose={() => setShowConfigDetails(false)}
                    style={{ padding: '8px 14px', fontSize: '0.82rem' }}
                >
                    <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                        ZZTEST account check — {configCheck.environment}: {(configCheck.checks?.length ?? 0) - configCheck.failures} of {configCheck.checks?.length ?? 0} pass
                    </div>
                    <div style={{ maxHeight: '32vh', overflowY: 'auto' }}>
                        <Table size="sm" style={{ fontSize: '0.78rem', marginBottom: 0, background: 'transparent' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '46px' }}></th>
                                    <th>Area</th>
                                    <th>Item</th>
                                    <th>Expected</th>
                                    <th>Actual</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Failures first, so they are not lost among the passing checks. */}
                                {[...(configCheck.checks || [])].sort((a, b) => Number(a.ok) - Number(b.ok)).map((check, index) => (
                                    <tr key={index}>
                                        <td style={{ color: check.ok ? '#16a34a' : '#b91c1c', fontWeight: 700 }}>{check.ok ? 'OK' : 'FAIL'}</td>
                                        <td>{check.area}</td>
                                        <td style={{ wordBreak: 'break-word' }}>{check.item}</td>
                                        <td style={{ wordBreak: 'break-word' }}>{check.expected}</td>
                                        <td style={{ wordBreak: 'break-word', fontWeight: check.ok ? 400 : 700 }}>{check.actual}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Alert>
            )}

            {/* ---------- Launch controls ----------
                No Refresh button: the tab reloads itself every 4s while a run is in flight and
                immediately after any launch or cancel, so the button had nothing to do that wasn't
                already happening. */}
            <Row style={{ marginBottom: '14px' }}>
                <Col xs={12} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Button size="sm" style={btnGold} disabled={launchBusy || allIds.length === 0} onClick={() => launchTests(allIds, 'tests')}>
                        <FontAwesomeIcon icon={faPlay} style={{ marginRight: 6 }} />Run All Tests
                    </Button>
                    <Button size="sm" style={btnGold} disabled={launchBusy || bulkIds.length === 0} onClick={() => launchTests(bulkIds, 'bulk tests')}>
                        <FontAwesomeIcon icon={faPlay} style={{ marginRight: 6 }} />Run Bulk Tests
                    </Button>
                    <Button size="sm" style={btnGold} disabled={launchBusy || singleIds.length === 0} onClick={() => launchTests(singleIds, 'single tests')}>
                        <FontAwesomeIcon icon={faPlay} style={{ marginRight: 6 }} />Run Single Tests
                    </Button>
                    <Button size="sm" style={btnGold} disabled={launchBusy || smokeIds.length === 0} onClick={() => launchTests(smokeIds, 'smoke test')}
                        title='Runs "[Single Test] Basic -> XLS" only — a quick post-deploy check that the backend is running'>
                        <FontAwesomeIcon icon={faPlay} style={{ marginRight: 6 }} />Run Smoke Test
                    </Button>

                    {/* Refreshes the test catalogue from the shipped UPDATE_TestDefinitions.sql, so the
                        script doesn't have to be hand-run against each environment's database. Orange to
                        set it apart from the launch actions — it WRITES to the TestDefinition table. */}
                    <span
                        style={{ display: 'inline-flex' }}
                        title={definitionsUpToDate
                            ? 'All tests up to date with current script'
                            : runStatus.isRunning
                                ? 'Cannot update while a run is in progress — the script rewrites the file lists a running test is reading from'
                                : inUpdateCooldown
                                    ? `Already updated ${formatDateTime(runStatus.definitionsUpdatedAt)} — available again in ${describeCooldown(cooldownRemaining)}`
                                    : definitionsDiffer
                                        ? `Run UPDATE_TestDefinitions.sql against this environment — it would change test ${definitionsStatus.differingDefinitionIds.map(id => `#${id}`).join(', ')}`
                                        : 'Run UPDATE_TestDefinitions.sql against this environment'}
                    >
                    <Button
                        size="sm"
                        style={{
                            backgroundColor: '#E8833A', borderColor: '#E8833A', color: '#1f2937',
                            fontWeight: 600, boxShadow: '0 3px 6px rgba(0, 0, 0, .45)',
                            pointerEvents: updateDefinitionsDisabled ? 'none' : undefined
                        }}
                        disabled={updateDefinitionsDisabled}
                        onClick={updateTestDefinitions}
                    >
                        <FontAwesomeIcon icon={faRotate} style={{ marginRight: 6 }} />
                        {updatingDefinitions
                            ? 'Updating…'
                            : inUpdateCooldown
                                ? `Updated — wait ${describeCooldown(cooldownRemaining)}`
                                : 'Update Test Definitions'}
                    </Button>
                    </span>

                    {/* The definitions are shared state, so say when they last changed rather than leaving
                        a disabled button unexplained. Reads the temporal stamp on TestDefinition, so it is
                        right even when someone ran the script by hand instead of pressing this. */}
                    {runStatus.definitionsUpdatedAt && (
                        <span style={{ fontSize: 12, color: COLORS.mutedText, alignSelf: 'center' }}>
                            Definitions last updated {formatDateTime(runStatus.definitionsUpdatedAt)}
                        </span>
                    )}

                    <Button
                        size="sm"
                        variant="outline-secondary"
                        style={{ fontWeight: 600 }}
                        disabled={downloadingDiffLogs}
                        title="Download every diff log from today's failed files as one zip, with a folder per test and run"
                        onClick={downloadTodaysDiffLogs}
                    >
                        {downloadingDiffLogs
                            ? <Spinner animation="border" size="sm" style={{ marginRight: 6 }} />
                            : <FontAwesomeIcon icon={faDownload} style={{ marginRight: 6 }} />}
                        Download Today's Diff Logs
                    </Button>

                    {/* Only offered while a run is actually cancellable — there is nothing to cancel
                        otherwise, and it must not reappear once cancellation is already under way. */}
                    {(runStatus.isRunning || cancelling) && (
                        <Button size="sm" variant="danger" disabled={!canCancel} onClick={cancelTests}>
                            <FontAwesomeIcon icon={faStop} style={{ marginRight: 6 }} />
                            {runStatus.isCancelling || cancelling ? 'Cancelling…' : 'Cancel All Active Tests'}
                        </Button>
                    )}
                </Col>
            </Row>

            {loadError && (
                <Alert variant="danger" style={{ padding: '8px 14px', fontSize: '0.88rem' }}>
                    {loadError}
                </Alert>
            )}

            {banner && (
                <Alert variant={banner.variant} dismissible onClose={() => setBanner(null)} style={{ padding: '8px 14px', fontSize: '0.88rem' }}>
                    {banner.message}
                </Alert>
            )}

            {/* Run notice: shown for RUN_NOTICE_TIMEOUT_MS when a new run is first seen (the Running now
                table carries the detail after that), and for as long as a cancellation is winding down.
                Launching is no longer blocked by a running run (only tests already running are), so the
                notice no longer says it is. */}
            {runStatus.isRunning && (runStatus.isCancelling || runNoticeVisible) && (
                <Alert
                    variant={runStatus.isCancelling ? 'warning' : 'info'}
                    style={{ padding: '8px 14px', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                    <Spinner animation="border" size="sm" />
                    {runStatus.isCancelling ? (
                        <span>
                            Cancelling{runStatus.processId ? ` (Process ${runStatus.processId})` : ''}. No further
                            files will be started; the {activeItems.length} file{activeItems.length === 1 ? '' : 's'}
                            {' '}already in the conversion queue will finish first — a queued conversion cannot be
                            recalled, and stopping mid-pipeline would strand those statements.
                        </span>
                    ) : (
                        <span>
                            A regression run is in progress (Process {runStatus.processId}
                            {runStatus.requestedBy ? `, started by ${runStatus.requestedBy}` : ''}
                            {runStatus.startedAt ? ` at ${formatDateTime(runStatus.startedAt)}` : ''}).
                        </span>
                    )}
                </Alert>
            )}

            {/* ---------- Running now ---------- */}
            {/* Lifted out of the main list while a run is in flight; each test drops back down into
                the list below, carrying its result, as soon as its run reaches a terminal status. */}
            {runningDefinitions.length > 0 && (
                <Card className="shadow-sm rounded-3" style={{ marginBottom: '16px', border: 'none', borderLeft: `3px solid ${COLORS.gold}` }}>
                    <Card.Body className="p-3">
                        <div style={{ fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: '#1f2937', marginBottom: '8px' }}>
                            Running now — {runningDefinitions.length} test{runningDefinitions.length === 1 ? '' : 's'},
                            {' '}{slotSummary}
                        </div>
                        {/* Own scroll region, sized in vh so it grows with the window. Sticky headers
                            keep the columns readable while scrolling a long run. */}
                        <div style={{ maxHeight: '42vh', overflowY: 'auto' }}>
                        <Table hover size="sm" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                            <thead>
                                {sectionHeaderRow('running', 'Status')}
                            </thead>
                            <tbody>
                                {runningDefinitions.map(definition => renderDefinitionRows(definition, true))}
                            </tbody>
                        </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* ---------- Recently completed ----------
                Tests whose run finished in the last RECENTLY_COMPLETED_MINUTES, so a run you just watched
                does not vanish into the full list; each moves back to the Test List once its time is up. */}
            {recentlyCompletedDefinitions.length > 0 && (
                <Card className="shadow-sm rounded-3" style={{ marginBottom: '16px', border: 'none', borderLeft: '3px solid #16a34a' }}>
                    <Card.Body className="p-3">
                        <div style={{ fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: '#1f2937', marginBottom: '8px' }}>
                            Recently completed — {recentlyCompletedDefinitions.length} test{recentlyCompletedDefinitions.length === 1 ? '' : 's'}
                            <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: '#9ca3af', marginLeft: '8px' }}>
                                finished in the last {RECENTLY_COMPLETED_MINUTES} minutes
                            </span>
                        </div>
                        <div style={{ maxHeight: '36vh', overflowY: 'auto' }}>
                            <Table hover size="sm" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                                <thead>
                                    {sectionHeaderRow('recent', 'Last Run')}
                                </thead>
                                <tbody>
                                    {recentlyCompletedDefinitions.map(definition => renderDefinitionRows(definition, false))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* ---------- Test list ---------- */}
            <Card className="shadow-sm rounded-3 border-0">
                <Card.Body className="p-3">
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: '#1f2937', marginBottom: '8px' }}>
                        Test List
                    </div>

                    {/* Scope and output-format filters. OR within a group, AND across groups, so
                        "Single + CDEX" narrows to single-scope CDEX tests. Nothing selected = show all. */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                        {['Single', 'Bulk', '|', 'Basic', 'Grid', 'JIB', '|', 'CDEX', 'XLS', 'OgsysPcxTxt'].map((filter, index) => {
                            // Visual separator between the scope / family / format groups.
                            if (filter === '|') {
                                return <span key={`sep-${index}`} style={{ width: '1px', height: '18px', background: '#e5e7eb', margin: '0 2px' }} />;
                            }

                            const isActive = activeFilters.includes(filter);
                            return (
                                <Button
                                    key={filter}
                                    size="sm"
                                    onClick={() => toggleFilter(filter)}
                                    style={{
                                        padding: '2px 12px',
                                        fontSize: '0.78rem',
                                        fontWeight: 600,
                                        borderRadius: '999px',
                                        backgroundColor: isActive ? COLORS.gold : '#ffffff',
                                        borderColor: isActive ? COLORS.gold : '#d1d5db',
                                        color: isActive ? COLORS.black : '#4b5563',
                                        boxShadow: 'none'
                                    }}
                                >
                                    {filter}
                                </Button>
                            );
                        })}

                        {activeFilters.length > 0 && (
                            <Button
                                variant="link"
                                size="sm"
                                style={{ padding: '0 4px', fontSize: '0.78rem', color: '#6b7280' }}
                                onClick={() => setActiveFilters([])}
                            >
                                Clear
                            </Button>
                        )}

                        <span style={{ fontSize: '0.78rem', color: '#9ca3af', marginLeft: 'auto' }}>
                            {idleDefinitions.length} of {listDefinitions.length} shown
                        </span>
                    </div>

                    <div style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                    <Table hover size="sm" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                        <thead>
                            {sectionHeaderRow('list', 'Last Run')}
                        </thead>
                        <tbody>
                            {definitions.length === 0 && (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0' }}>
                                        {loadError
                                            // Never claim the table is empty when we simply failed to read it.
                                            ? 'The regression tests could not be loaded — see the error above.'
                                            : 'No test definitions were found. Seed them with Database_Scripts/Ad Hoc Queries/Regression Testing/INSERT_TestDefinitions.sql.'}
                                    </td>
                                </tr>
                            )}

                            {definitions.length > 0 && idleDefinitions.length === 0 && (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0' }}>
                                        {activeFilters.length > 0
                                            ? 'No tests match the selected filters.'
                                            : 'Every test is currently running or recently completed — see above.'}
                                    </td>
                                </tr>
                            )}

                            {idleDefinitions.map(definition => renderDefinitionRows(definition, false))}
                        </tbody>
                    </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* ---------- Legacy Test List ----------
                OGSYS PCX output is retired, so these definitions are kept for their history but are
                not runnable — separated out so the live list above is only tests that still matter. */}
            {legacyDefinitions.length > 0 && (
                <Card className="shadow-sm rounded-3 border-0" style={{ marginTop: '16px', opacity: 0.85 }}>
                    <Card.Body className="p-3">
                        <div style={{ fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: '#6b7280', marginBottom: '4px' }}>
                            Legacy Test List
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '10px' }}>
                            Disabled — OGSYS PCX output is retired. Kept for reference; these cannot be run.
                        </div>

                        <div style={{ maxHeight: '30vh', overflowY: 'auto' }}>
                            <Table hover size="sm" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                                <thead>
                                    {sectionHeaderRow('legacy', 'Last Run')}
                                </thead>
                                <tbody>
                                    {legacyDefinitions.map(definition => renderDefinitionRows(definition, false))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {diffViewerItem && (
                <DiffLogViewer
                    item={diffViewerItem}
                    getAccessTokenSilently={getAccessTokenSilently}
                    onDownload={() => downloadDiffLog(diffViewerItem)}
                    onClose={() => setDiffViewerItem(null)}
                />
            )}
        </div>
    );
}
